import React, { useContext, useState } from "react";
import { Button } from "@mui/material";
import { getDocs } from "firebase/firestore";
import { eachDayOfInterval } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import PropertyContext from "../../contexts/PropertyContext";
import { propertyCollection } from "../../utils/firestorePaths";
import { drawReportHeader, stampPageNumbers } from "../../utils/reports/pdfChrome";
import { downloadBlob } from "../../utils/reports/downloadBlob";
import { buildReportFilename } from "../../utils/reports/reportFilename";
import { formatDateForFirestore } from "../../utils/dispatchUtils";
import ReportDateRangeDialog from "./shared/ReportDateRangeDialog";

/**
 * Completed Dispatch by Employee — assigned vs. completed dispatch tasks per
 * employee over a date range. Dispatch tasks are completed in the mobile app,
 * which sets completed:true on each task document.
 *
 * Path: properties/{id}/dispatches/{YYYY-MM-DD}/{employeeDocId}/{taskId}
 */
export default function GenerateCompletedDispatchByEmployee({ user }) {
  const { selectedProperty, selectedClient } = useContext(PropertyContext);
  const [open, setOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async ({ start, end }) => {
    setIsGenerating(true);
    try {
      // Load employees once.
      const empSnap = await getDocs(
        propertyCollection(selectedClient, selectedProperty, "employees")
      );
      const employees = empSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const days = eachDayOfInterval({ start, end });

      // Aggregate assigned/completed per employee across all days.
      const agg = new Map(); // name -> { assigned, completed }
      await Promise.all(
        days.flatMap((day) => {
          const dateStr = formatDateForFirestore(day);
          return employees.map(async (emp) => {
            const docId = emp.userId || emp.id;
            const ref = propertyCollection(
              selectedClient,
              selectedProperty,
              "dispatches",
              dateStr,
              docId
            );
            const snap = await getDocs(ref);
            if (snap.empty) return;
            const name = emp.name || docId;
            const cur = agg.get(name) || { assigned: 0, completed: 0 };
            snap.docs.forEach((t) => {
              const data = t.data();
              cur.assigned += 1;
              if (data.completed === true) cur.completed += 1;
            });
            agg.set(name, cur);
          });
        })
      );

      if (agg.size === 0) {
        alert("No dispatch tasks found in that date range.");
        return;
      }

      const rows = Array.from(agg.entries()).sort(
        (a, b) => b[1].completed - a[1].completed
      );
      const totals = rows.reduce(
        (acc, [, v]) => ({
          assigned: acc.assigned + v.assigned,
          completed: acc.completed + v.completed,
        }),
        { assigned: 0, completed: 0 }
      );

      const pct = (c, t) => (t > 0 ? `${Math.round((c / t) * 100)}%` : "—");

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const contentTop = drawReportHeader(pdf, {
        title: "Completed Dispatch by Employee",
        dateRange: { start, end },
        generatedBy: user?.fullName || user?.personalEmail || "Unknown",
      });

      const body = rows.map(([name, v]) => [
        name,
        String(v.assigned),
        String(v.completed),
        pct(v.completed, v.assigned),
      ]);
      body.push([
        { content: "TOTAL", styles: { fontStyle: "bold" } },
        { content: String(totals.assigned), styles: { fontStyle: "bold", halign: "right" } },
        { content: String(totals.completed), styles: { fontStyle: "bold", halign: "right" } },
        { content: pct(totals.completed, totals.assigned), styles: { fontStyle: "bold", halign: "right" } },
      ]);

      autoTable(pdf, {
        startY: contentTop,
        head: [["Employee", "Assigned", "Completed", "Completion"]],
        body,
        styles: { font: "helvetica", fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [40, 40, 40], textColor: 255 },
        columnStyles: {
          1: { halign: "right" },
          2: { halign: "right" },
          3: { halign: "right" },
        },
        margin: { left: 12, right: 12 },
        theme: "striped",
      });

      stampPageNumbers(pdf);
      downloadBlob(
        pdf.output("blob"),
        buildReportFilename("Completed_Dispatch_By_Employee", { ext: "pdf", start, end })
      );
      setOpen(false);
    } catch (e) {
      console.error("Error generating Completed Dispatch by Employee:", e);
      alert("Error generating report. See console for details.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      <Button variant="contained" fullWidth onClick={() => setOpen(true)}>
        Completed Dispatch by Employee
      </Button>
      <ReportDateRangeDialog
        open={open}
        title="Completed Dispatch by Employee"
        description="Assigned vs. completed dispatch tasks per employee over the date range."
        busy={isGenerating}
        onClose={() => setOpen(false)}
        onSubmit={handleGenerate}
      />
    </>
  );
}
